import { messageI18n } from '../../../helpers/i18n/toastI18n';
import {
  displayFeedSourceLabel,
  FEED_SCREEN_SHARE,
  FEED_SOURCE_PLACEHOLDER,
  isFeedPlaceholder,
} from '../../../helpers/i18n/realtimeFeedSource';
import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  MdCamera,
  MdKeyboardArrowDown,
  MdPlayArrow,
} from 'react-icons/md';

export default function FeedSourceSelector({
  selectedFeedSource,
  setSelectedFeedSource,
  showFeedDropdown,
  setShowFeedDropdown,
  inputDevices,
  setDeviceId,
  nextStep,
  startVideoStream,
  setGlobalState,
  globalState,
}) {
  const { t } = useTranslation('RealtimeExam');

  return (
    <div className="w-full mx-auto p-6 bg-card-color-100 dark:bg-gray-800">
      <div className="flex items-center gap-3 mb-2">
        <MdCamera className="text-xl text-title-page-color-100 dark:text-white" />
        <h1 className="text-xl font-medium text-title-page-color-100 dark:text-white">
          {t('feedSelector.title')}
        </h1>
      </div>

      <p className="text-title-page-color-400 dark:text-gray-400 mb-8 text-sm">
        {t('feedSelector.description')}
      </p>

      <div className="mb-8">
        <label className="block text-lg font-medium text-title-page-color-100 dark:text-white mb-4">
          {t('feedSelector.videoFeedSource')}
        </label>

        <div className="relative">
          <button
            onClick={() => setShowFeedDropdown(!showFeedDropdown)}
            className="w-full flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
          >
            <div className="flex items-center gap-3">
              <span className="text-gray-900 dark:text-gray-100 text-sm">
                {displayFeedSourceLabel(selectedFeedSource, t)}
              </span>
              {!isFeedPlaceholder(selectedFeedSource) && (
                <span className="px-1.5 py-0.5 bg-black dark:bg-gray-900 text-white text-xs rounded-full">
                  {t('feedSelector.connected')}
                </span>
              )}
            </div>
            <MdKeyboardArrowDown
              className={`text-gray-600 dark:text-gray-400 transition-transform ${showFeedDropdown ? 'rotate-180' : ''}`}
            />
          </button>

          {showFeedDropdown && (
            <div className="absolute top-full left-0 text-sm right-0 mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg shadow-lg z-10">
              <button
                onClick={() => {
                  setDeviceId('');
                  setSelectedFeedSource(FEED_SCREEN_SHARE);
                  setShowFeedDropdown(false);
                  setGlobalState((prevState) => ({
                    ...prevState,
                    optionValueDevice: 'Share',
                  }));
                }}
                className="w-full text-left p-4 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors border-b border-gray-100 dark:border-gray-600"
              >
                <div className="flex items-center justify-between">
                  <span className="text-gray-900 dark:text-gray-100">
                    {t('feedSelector.screenShare')}
                  </span>
                  <span className="px-3 py-1 bg-green-600 text-white text-xs rounded-full">
                    {t('feedSelector.available')}
                  </span>
                </div>
              </button>

              {inputDevices.map((source) => (
                <button
                  key={source.deviceId}
                  onClick={() => {
                    setDeviceId(source.deviceId);
                    setSelectedFeedSource(source.label);
                    setShowFeedDropdown(false);
                    setGlobalState((prevState) => ({
                      ...prevState,
                      optionValueDevice: 'USB',
                    }));
                  }}
                  className="w-full text-left p-4 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors border-b border-gray-100 dark:border-gray-600 last:border-b-0"
                >
                  <div className="flex items-center justify-between">
                    <span className="text-gray-900 dark:text-gray-100">
                      {source.label}
                    </span>
                    <span className="px-3 py-1 bg-blue-600 text-white text-xs rounded-full">
                      USB
                    </span>
                  </div>
                </button>
              ))}

              {inputDevices.length === 0 && (
                <div className="p-4 text-center text-gray-500 dark:text-gray-400 text-sm">
                  {t('feedSelector.noUsbUseScreenShare')}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="flex justify-end">
        <button
          onClick={() => {
            if (isFeedPlaceholder(selectedFeedSource)) {
              messageI18n.warning('feed.selectVideoFeedToStart');
              return;
            }

            if (
              globalState.optionValueDevice === 'USB' &&
              (!globalState.deviceId || globalState.deviceId.trim() === '')
            ) {
              messageI18n.error('feed.selectValidCamera');
              return;
            }

            startVideoStream(globalState.deviceId, globalState.optionValueDevice);
            setGlobalState((prevState) => ({
              ...prevState,
              isStart: true,
            }));
            if (nextStep) nextStep();
            messageI18n.success('feed.videoStreamStarted');
          }}
          className="flex items-center text-sm gap-2 px-3 py-1.5 bg-black dark:bg-gray-900 text-white rounded-lg hover:bg-gray-800 dark:hover:bg-gray-700 transition-colors"
        >
          <MdPlayArrow className="text-sm" />
          {t('feedSelector.startStream')}
        </button>
      </div>
    </div>
  );
}
